import { useActionsForCurrentCountry } from "../../../../firebase/db/recommendedActions";
import { useFullMathScope } from "../../../hooks/firebaseHooks";
import { userAnswersSelectors } from "../../../store/slice/userAnswersSlice";
import { ButtonMedium, ButtonLarge } from "../../ui/buttons";
import { createTranslationKey } from "../utils";
import { RegionalNamespaces } from "@pslifestyle/common/src/constants/translations";
import {
  GRANULAR_MULTIPLIER,
  calculateImpact,
} from "@pslifestyle/common/src/dataBuilders/actionsBuilder";
import { GranularActionOption } from "@pslifestyle/common/src/schemas";
import { ActionImpact } from "@pslifestyle/common/src/types/planTypes";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Fragment } from "react/jsx-runtime";

type GranularActionProps = {
  actionId: string;
  granularOptions: GranularActionOption[];
  selectedOption?: GranularActionOption;
  countryCode: string;
  onOptionSelect: (
    value: GranularActionOption,
    updatedImpacts: ActionImpact,
  ) => void;
  onClose: () => void;
};
export const GranularActionOptions = ({
  actionId,
  granularOptions,
  countryCode,
  selectedOption,
  onOptionSelect,
  onClose,
}: GranularActionProps) => {
  const { t } = useTranslation([
    "recommendations",
    "common",
    RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
  ]);
  const { fullMathScope } = useFullMathScope();
  const { data } = useActionsForCurrentCountry();

  const actions = data?.[1] ?? [];

  const totalFootprint = useSelector(userAnswersSelectors.totalFootprint);

  const handleGranularOptionSelect = (selectedOption: GranularActionOption) => {
    const formulas =
      actions.find((action) => action.id === actionId)?.impactFormulas ?? [];
    const mathScope = {
      ...fullMathScope,
      [GRANULAR_MULTIPLIER]: parseFloat(selectedOption.value),
    };
    const granularImpact = calculateImpact(mathScope, formulas);
    const impactValues = {
      calculatedImpact: granularImpact.value,
      percentReduction: (granularImpact.value / totalFootprint) * 100,
      calculateImpactError: granularImpact.error,
      calculateImpactErrorSource: granularImpact.errorSource,
      calculateImpactErrorMessage: granularImpact.errorMessage,
      calculateImpactErrorFormula: granularImpact.evaluatedFormula,
    };
    onOptionSelect(selectedOption, impactValues);
  };
  return (
    <div className="flex flex-col items-start  px-5 w-full gap-1">
      {granularOptions.map((granularOption) => {
        const isSelected =
          selectedOption?.optionTranslationKey ===
          granularOption.optionTranslationKey;
        return (
          <Fragment key={granularOption.optionTranslationKey}>
            <ButtonMedium
              className="w-full"
              buttonClassName="w-full"
              theme={isSelected ? "goalCTA" : "secondary"}
              onClick={() => handleGranularOptionSelect(granularOption)}
            >
              <div
                className={`${isSelected ? "border-4" : "border-neutral-20 border"} mr-2 h-6 w-6 rounded-full`}
              />
              {t(
                createTranslationKey(
                  granularOption.optionTranslationKey,
                  countryCode,
                ),
                { ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS },
              )}
            </ButtonMedium>
          </Fragment>
        );
      })}
      <ButtonLarge
        theme="tertiary"
        onClick={onClose}
        buttonClassName="w-full my-3"
        className="w-full justify-center"
        icon={{ size: "medium", type: "ChevronUp", position: "left" }}
      >
        {t("close", { ns: "common" })}
      </ButtonLarge>
    </div>
  );
};
